import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
  Link as MuiLink,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { apiService, ApiError } from '../../services/apiService';

/**
 * "Forgot password" entry point. Submits the user's email so the backend can
 * send a reset link. The response is intentionally generic (it never reveals
 * whether an account exists). In non-production the backend also returns the
 * reset token, which we surface as a direct link to ease testing.
 */
const ForgotPasswordForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devResetPath, setDevResetPath] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsLoading(true);
    setError(null);
    setDevResetPath(null);
    try {
      const res = await apiService.requestPasswordReset(email.trim());
      setSubmitted(true);
      const devToken = res.data?.reset_token;
      if (devToken) {
        setDevResetPath(`/reset-password?token=${encodeURIComponent(devToken)}`);
      }
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? (apiService.formatErrorDetail(err) || err.message)
          : 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography component="h1" variant="h4" gutterBottom>
              Reset your password
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter the email for your account and we'll send you a link to choose a new password.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {submitted ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                If an account exists for <strong>{email.trim()}</strong>, a password reset link is on its way.
                Check your inbox (and spam folder).
              </Alert>
              {devResetPath && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Email isn't configured on this environment. Use this link to continue:{' '}
                  <MuiLink component={RouterLink} to={devResetPath}>
                    Set a new password
                  </MuiLink>
                </Alert>
              )}
              <Button fullWidth variant="contained" component={RouterLink} to="/login" sx={{ mt: 1 }}>
                Back to sign in
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                type="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={isLoading || !email.trim()}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Send reset link'}
              </Button>
              <Button fullWidth variant="text" component={RouterLink} to="/login" disabled={isLoading}>
                Back to sign in
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ForgotPasswordForm;
